import React from 'react';
import { clsx } from 'clsx';

export interface ImpactItem {
  label: string;
  value: number | string;
  /** Render the value in destructive tone (e.g. records purged, people losing access). */
  emphasize?: boolean;
}

interface Props {
  items: ImpactItem[];
  loading?: boolean;
  error?: boolean;
  title?: string;
  className?: string;
}

/**
 * Impact preview shown before a destructive delete (P1) — a compact "what gets
 * deleted" list of counts. Deliberately says nothing about restore/grace: the
 * retention window is internal-only and never surfaced to users.
 */
const DeletionImpactPreview: React.FC<Props> = ({
  items,
  loading,
  error,
  title = 'What gets deleted',
  className,
}) => {
  if (loading) {
    return (
      <div className={clsx('rounded-[10px] border border-border bg-muted/50 p-3', className)}>
        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
          {title}
        </div>
        <div className="space-y-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-4 rounded bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={clsx(
          'rounded-[10px] border border-border bg-muted/50 p-3 text-[12.5px] text-muted-foreground',
          className,
        )}
      >
        Couldn't load the deletion impact. You can still proceed.
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div
      className={clsx('rounded-[10px] border border-destructive/30 bg-destructive/[0.04] p-3', className)}
    >
      <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
        {title}
      </div>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.label} className="flex items-baseline justify-between gap-3 text-[13px]">
            <span className="text-muted-foreground">{it.label}</span>
            <span
              className={clsx(
                'font-semibold tabular-nums',
                it.emphasize ? 'text-destructive' : 'text-foreground',
              )}
            >
              {it.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DeletionImpactPreview;
